import { MenuService } from './../menu.service'

import { Component, DestroyRef, ElementRef, inject, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateDirective } from '@ngx-translate/core';
import { OrderByPipe } from '@shared/pipes/order-by.pipe';
import { filter } from 'rxjs/operators';

import { EventService } from '@common/event.service';
import { StorageService } from '@common/storage.service';
import { WindowService } from '@common/window.service';
import { IdentityClientService } from '@identity/identity-client.service';
import { IdentityCommonService } from '@identity/identity-common.service';
import { IdentityConfigurationService } from '@identity/identity-configuration.service';
import { IdentityService } from '@identity/identity.service';
import { SharedConstants } from '@shared/constants';

const MENU_SELECTOR = '#sasMenu';
const HIDE_MENU_KEY = 'HIDE_MENU';
const MOBILE_MAX_SIZE = 767; // px

@Component({
    selector: 'sas-menu',
    standalone: true,
    imports: [RouterModule, TranslateDirective, OrderByPipe],
    templateUrl: './menu.component.html',
    styleUrl: './menu.component.scss'
})

export class MenuComponent {
    protected menuService = inject(MenuService);
    private destroyRef = inject(DestroyRef);
    private identityConfigService = inject(IdentityConfigurationService);
    private identityClientService = inject(IdentityClientService);
    private identityService = inject(IdentityService);
    private eventService = inject(EventService);
    private storageService = inject(StorageService);
    private windowService = inject(WindowService);
    private identityCommonService = inject(IdentityCommonService);
    private RELOAD_MENU_EVENT = SharedConstants.RELOAD_MENU_EVENT;

    protected clientUrl: string = this.identityClientService.get().url;
    protected serviceUrl: string = this.identityConfigService.getServiceUrl();
    private hideMenu = this.identityConfigService.getConfiguration(HIDE_MENU_KEY);
    protected items: Array<any> = [];
    protected tabIndex: number = 0;
    private currentStateName: string = '';
    private menuElement: any;
    private isMobile = false;
    readonly SIGN_OUT_CODE = 'SIGN_OUT';
    readonly SIGN_IN_CODE = 'SIGN_IN';
    protected readonly landpage: any = {
        code: 'LANDPAGE',
        external: false,
        index: 0,
        value: 'landpage'
    };

    constructor(private router: Router, private el: ElementRef, private renderer: Renderer2) {
        this.eventService.getEvent(this.RELOAD_MENU_EVENT)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((ev) => {
                this.hideMenu = this.identityConfigService.getConfiguration(HIDE_MENU_KEY);
                this.loadMenu();
            });
    }

    ngOnInit() {
        this.getCurrentState();

        this.menuElement = this.el.nativeElement.querySelector(MENU_SELECTOR);
        //    $scope.$on('setFocusFirstEvent', function (event) {
        //        angular.element('#sas-menu .menu-item > a')[0].focus();
        //    })
    }

    ngAfterOnInit() {
        this.checkMenuConfiguration();
    }

    ngDoCheck() {
        this.tabIndex = this.menuElement.classList.contains('inactive') ? -1 : 0;
    }

    private getCurrentState() {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            this.currentStateName = this.router.url;
        });
    }

    ensureOpen() {
        if (this.menuElement.classList.contains('inactive') && !this.currentStateName.includes('landpage')) {
            this.renderer.removeClass(this.menuElement, 'inactive');
        }
    }

    private closeMenu() {
        this.checkWindowSize();
        if (this.isMobile && !this.menuElement.classList.contains('inactive')) {
            this.renderer.addClass(this.menuElement, 'inactive');
        }
    };

    selectOption(opt: any) {
        this.closeMenu();
    }

    private loadMenu() {
        if (this.hideMenu === 'true') {
            this.renderer.addClass(this.menuElement, 'inactive');
            return [];
        }
        this.serviceUrl = this.identityConfigService.getServiceUrl();
        return this.identityService.getModules().subscribe((resp: Array<any>) => {
            this.items = this.mapMenuItems(resp);

            return this.items;
        });
    }

    private mapMenuItems(items: Array<any>): Array<any> {
        return items.map((item: any) => {
            return {
                index: item.index,
                code: item.code.trim(),
                value: item.value,
                external: item.external
            }
        });
    }

    private checkMenuConfiguration() {
        const configurations = JSON.parse(this.storageService.get('sas-auth-service-configuration') || '');
        const menuOpenByDefault = configurations.find((configuration: { key: string; }) => configuration.key === 'MENU_OPEN_BY_DEFAULT');

        if (menuOpenByDefault !== undefined && menuOpenByDefault.value === 'true') {
            this.ensureOpen();
        }
    }

    isMenuActive(opt: any): boolean {
        const current = this.currentStateName.toLowerCase();
        const normalized = opt.value
          ?.replace(/[A-Z]/g, (m: string) => `-${m.toLowerCase()}`)
          .toLowerCase();

        return current.includes(normalized);
    }

    private checkWindowSize() {
        this.isMobile = this.windowService.nativeWindow.innerWidth <= MOBILE_MAX_SIZE;
        return this.isMobile;
    }

    signOut() {
        this.identityCommonService.logout();
    }
}
